import React, { useState } from 'react';
import './upload-modal.css';
import { getCookieId } from '../../WebserverUser';
import { getApiEndpoint } from '../../config/oncorisk-config';



function UploadModal({ isOpen, onClose, cookieId: cookieIdProp, onUploadSuccess }) {
  const [uploadedFiles, setUploadedFiles] = useState({ vcf: null, dna: null, rna: null });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [currentFile, setCurrentFile] = useState(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  const cookieId = cookieIdProp || getCookieId();

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFiles(prev => ({ ...prev, [type]: file }));
    }
  };

  const handleClear = () => {
    setUploadedFiles({ vcf: null, dna: null, rna: null });
  };

  const CHUNK_SIZE = 75 * 1024 * 1024; // 75MB

  const handleSubmit = async () => {
    setIsUploading(true);
    setUploadProgress({});
    setUploadComplete(false);
    
    const batchUploadId = crypto.randomUUID();
    const filesForUpload = Object.entries(uploadedFiles)
      .filter(([, file]) => file)
      .map(([type, file]) => ({ type, file }));

    console.log('[UploadModal] submit job cookie:', cookieId);
    console.log('[UploadModal] batch:', batchUploadId, 'files:', filesForUpload.map(f => f.file.name));

    const filesMetadata = [];

    for (const { type, file } of filesForUpload) {
      setCurrentFile(file.name);
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      
      const fileId = crypto.randomUUID();
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const chunk = file.slice(start, start + CHUNK_SIZE);
        const fd = new FormData();
        fd.append('chunk', chunk);
        fd.append('batchUploadId', batchUploadId);
        fd.append('fileId', fileId);
        fd.append('originalFilename', file.name);
        fd.append('chunkIndex', i);
        fd.append('totalChunks', totalChunks);
        fd.append('fileType', file.type || 'application/octet-stream');
        try {
          // Log the body that's being sent (for FormData, we log key/value pairs)
          console.log(`[UploadModal] Uploading chunk ${i + 1}/${totalChunks} of ${file.name}:`, 
            Array.from(fd.entries()).reduce((obj, [key, value]) => {
              obj[key] = value instanceof Blob ? `[Blob size: ${value.size}]` : value;
              return obj;
            }, {})
          );
          
          await fetch(getApiEndpoint('uploadChunk'), {
            method: 'POST',
            body: fd,
            credentials: 'include'
          });
          
          // Update progress for this file
          const progress = Math.round(((i + 1) / totalChunks) * 100);
          setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
          
          console.log(`[UploadModal] uploaded chunk ${i + 1}/${totalChunks} of ${file.name}`);
        } catch (err) {
          console.error('[UploadModal] chunk upload error', err);
          setIsUploading(false);
          return;
        }
      }
      filesMetadata.push({
        fileId,
        originalFilename: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream', // This is the browser's detected MIME type
        designatedType: type // ('vcf', 'dna', or 'rna')
      });    }

    if (filesMetadata.length === 0) {
      console.warn('[UploadModal] No files uploaded successfully.');
      return;
    }

    try {
      const requestBody = { batchUploadId, files: filesMetadata, accountCookie: cookieId };
      console.log('[UploadModal] finalizing upload with body:', requestBody);
      
      const resp = await fetch(getApiEndpoint('finalizeUpload'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });
      const data = await resp.json().catch(() => ({}));
      console.log('[UploadModal] finalize response', data);
      setUploadComplete(true);
      
      // Call the callback if provided
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      console.error('[UploadModal] finalize error', err);
    } finally {
      setIsUploading(false);
    }

    handleClear();
    
    // Automatically close the modal only after upload is complete
    if (onClose && !isUploading) {
      setTimeout(() => onClose(), 1000); // Small delay to show completion
    }
  };

  if (!isOpen) return null;

  return (
    <div className="OCRP-upload-modal-overlay" onClick={isUploading ? null : onClose}>
      <div className="OCRP-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="OCRP-upload-modal-header">
          <div className="OCRP-upload-title">Upload Genomic Files</div>
          <button className="OCRP-upload-close" onClick={isUploading ? null : onClose} disabled={isUploading} style={isUploading ? {opacity: 0.5, cursor: 'not-allowed'} : {}}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <p className="OCRP-upload-description">
          Upload VCF, DNA, or RNA files for analysis. Supported formats include .vcf.gz, .json.gz and .tsv.
        </p>

        <div className="OCRP-upload-grid">
          <div className="OCRP-upload-card">
            <div className="OCRP-upload-icon">
              <i className="fas fa-file-medical"></i>
            </div>
            <h3>VCF File / JSON</h3>
            <p>Upload Variant Call Format or JSON .gz files containing genomic variations</p>
            <label className="OCRP-file-upload">
              <input type="file" accept=".vcf,.vcf.gz,.json,.json.gz,.gz" onChange={e => handleFileChange('vcf', e)} />
              <span>{uploadedFiles.vcf ? uploadedFiles.vcf.name : 'Choose File'}</span>
            </label>
            {uploadedFiles.vcf && (
              <div className="OCRP-file-details">
                <span>Size: {(uploadedFiles.vcf.size / (1024 * 1024)).toFixed(2)} MB</span>
              </div>
            )}
          </div>

          <div className="OCRP-upload-card">
            <div className="OCRP-upload-icon">
              <i className="fas fa-dna"></i>
            </div>
            <h3>DNA Sequence</h3>
            <p>Upload DNA sequence files (TSV format) for genomic analysis</p>
            <label className="OCRP-file-upload">
              <input type="file" accept=".tsv, .tsv.gz" onChange={e => handleFileChange('dna', e)} />
              <span>{uploadedFiles.dna ? uploadedFiles.dna.name : 'Choose File'}</span>
            </label>
            {uploadedFiles.dna && (
              <div className="OCRP-file-details">
                <span>Size: {(uploadedFiles.dna.size / (1024 * 1024)).toFixed(2)} MB</span>
              </div>
            )}
          </div>

          <div className="OCRP-upload-card">
            <div className="OCRP-upload-icon">
              <i className="fas fa-dna"></i>
            </div>
            <h3>RNA Sequence (Optional)</h3>
            <p>Upload RNA sequence files for expression analysis</p>
            <label className="OCRP-file-upload">
              <input type="file" accept=".bam,.fasta,.fastq" onChange={e => handleFileChange('rna', e)} />
              <span>{uploadedFiles.rna ? uploadedFiles.rna.name : 'Choose File (Optional)'}</span>
            </label>
            {uploadedFiles.rna && (
              <div className="OCRP-file-details">
                <span>Size: {(uploadedFiles.rna.size / (1024 * 1024)).toFixed(2)} MB</span>
              </div>
            )}
          </div>
        </div>
        <div className="OCRP-upload-options">
          <div className="OCRP-analysis-params">
            {/* Analysis parameters can be added here in the future */}
          </div>
          
          {isUploading && (
            <div className="OCRP-upload-progress-container">
              <h3>Upload Progress</h3>
              <div className="OCRP-current-file">
                <span>Current file: {currentFile}</span>
              </div>
              
              {Object.entries(uploadProgress).map(([filename, progress]) => (
                <div key={filename} className="OCRP-file-progress">
                  <div className="OCRP-file-progress-name">{filename}</div>
                  <div className="OCRP-progress-bar">
                    <div 
                      className="OCRP-progress-fill" 
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="OCRP-progress-percentage">{progress}%</div>
                </div>
              ))}
              
              {uploadComplete && (
                <div className="OCRP-upload-complete">
                  <i className="fas fa-check-circle"></i> Upload complete! Processing...
                </div>
              )}
            </div>
          )}
        </div>
        <div className="OCRP-upload-actions">
          <button className="OCRP-button OCRP-button-secondary" onClick={handleClear}>Clear All</button>
          <button
            className="OCRP-button OCRP-button-primary"
            onClick={handleSubmit}
            disabled={((!uploadedFiles.vcf && !uploadedFiles.dna) || isUploading)}
          >
            {isUploading ? 'Uploading...' : 'Submit Analysis'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadModal;
